import { FC, useEffect, useState } from 'react';

import { useIsAdminRoute } from '../use-is-admin-route';
import BeachBg from './images/backgrounds/day.webp';
import BlueTube from './images/blue-tube.png';
import Flamingo from './images/flamingo.png';
import GreenTube from './images/green-tube.png';
import MagentaTube from './images/magenta-tube.png';
import OrangeTube from './images/orange-tube.png';
import Pineapple from './images/pineapple.png';
import PinkTube from './images/pink-tube.png';
import PurpleTube from './images/purple-tube.png';
import RedTube from './images/red-tube.png';
import Unicorn from './images/unicorn.png';
import YellowFloat from './images/yellow-float.png';

type CelestialBody = 'sun' | 'moon';

const STARS = Array.from({ length: 40 }, (_, i) => ({
	x: ((i * 7919 + 1301) % 97) + 1,
	y: ((i * 6271 + 883) % 93) + 2,
	size: 1.5 + ((i * 3571) % 3),
	delay: ((i * 4219) % 5000) / 1000,
}));

const SunSvg: FC<{ size: number; filter?: string }> = ({ size, filter }) => (
	<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" width={size} height={size} style={{ filter }}>
		<path
			d="m20.5 59.7 7-7.2c-2.5-.5-4.8-1.5-6.9-2.9zm23-55.4-7 7.2c2.5.5 4.8 1.5 6.9 2.9zm-39.2 39.2 10.1-.1c-1.4-2.1-2.4-4.4-2.9-6.9zm55.4-23-10.1.1c1.3 2.1 2.3 4.4 2.9 6.9zm-55.4 0 7.2 7c.5-2.5 1.5-4.8 2.9-6.9zm55.4 23-7.2-7c-.5 2.5-1.5 4.8-2.9 6.9zm-39.2-39.2.1 10.1c2.1-1.3 4.4-2.3 6.9-2.9zm23 55.4-.1-10.1c-2.1 1.4-4.4 2.4-6.9 2.9z"
			fill="#ffe62e"
		/>
		<g fill="#ffce31">
			<path d="m14.8 44-4 9.3 9.3-4c-2.1-1.5-3.9-3.3-5.3-5.3m34.4-24 4-9.3-9.2 4c2 1.5 3.8 3.3 5.2 5.3m-37.8 8.3-9.4 3.7 9.4 3.7c-.3-1.2-.4-2.4-.4-3.7s.1-2.5.4-3.7m41.2 7.4 9.4-3.7-9.4-3.7c.2 1.2.4 2.5.4 3.7 0 1.3-.1 2.5-.4 3.7m-32.6-20.9-9.3-4 4 9.3c1.5-2.1 3.3-3.9 5.3-5.3m24 34.4 9.3 4-4-9.3c-1.5 2.1-3.3 3.9-5.3 5.3m-8.3-37.8-3.7-9.4-3.7 9.4c1.2-.2 2.5-.4 3.7-.4s2.5.1 3.7.4m-7.4 41.2 3.7 9.4 3.7-9.4c-1.2.3-2.4.4-3.7.4s-2.5-.1-3.7-.4" />
			<circle cx="32" cy="32" r="19" />
		</g>
	</svg>
);

const MoonSvg: FC<{ size: number }> = ({ size }) => (
	<svg viewBox="0 0 128 128" xmlns="http://www.w3.org/2000/svg" width={size} height={size}>
		<path
			d="m99.32 28.84-5.81-19.34-1.98-2.66s-.15-.18-.17-.24c-.06-.25-.13-.66.11-.97.18-.23.49-.29.85-.26.14.01.48.14 1.02.44 5.31 3 28.21 21.75 30.14 52.02 1.09 17.1-5.15 35.4-18.27 48.16-12.46 12.11-31.28 19.65-51.98 16.7-30.94-4.41-44.22-23.21-47.69-29.42-.61-1.1-.91-1.8-.97-1.97-.82-2.15-.6-3.24.17-3.49.2-.06.65.02.9.3 1 1.12 2.82 1.85 2.82 1.85l12.88 10.43 63.89 4.84 22.65-49.38z"
			fill="#ffb803"
		/>
		<path
			d="m73.65 87.67c15.97-9.9 23.77-26.72 24.39-42.28.9-22.79-6.68-38.8-6.68-38.8s14.01 11.5 21.68 28.08 9 43.87-7.78 63.4c-18.45 21.45-43.18 22.72-58.73 18.7-32.27-8.35-41.09-28.83-41.09-28.83s17.78 9.95 37.77 8.9c14.88-.78 22.54-4.27 30.44-9.17"
			fill="#ffca29"
		/>
	</svg>
);

const getSceneState = (hour: number) => {
	const isDay = hour >= 5 && hour < 19;

	let progress: number;
	if (isDay) {
		progress = (hour - 5) / 14;
	} else {
		const nightHour = hour >= 19 ? hour - 19 : hour + 5;
		progress = nightHour / 10;
	}

	const x = 10 + progress * 80;
	const yBase = 85;
	const yPeak = 15;
	const y = yBase - (yBase - yPeak) * (1 - Math.pow(2 * progress - 1, 2));

	let skyGlow: string;
	let skyGlowSize: number;
	let skyGlowOpacity: number;
	let bodyGlow: string;
	let bodyGlowOpacity: number;
	let bodySize: number;
	let bodyColor: string;
	let bodyType: CelestialBody;
	let bodyFilter: string | undefined;
	let bodyOpacity: number;
	let starsOpacity: number;
	let beachFilter: string;
	let skyTint: string;
	let skyTintOpacity: number;

	if (hour >= 4 && hour < 5) {
		skyGlow = 'radial-gradient(ellipse, rgba(165, 180, 252, 0.1) 0%, transparent 55%)';
		skyGlowSize = 600;
		skyGlowOpacity = 0.2;
		bodyGlow = 'radial-gradient(circle, rgba(199, 210, 254, 0.1) 0%, transparent 60%)';
		bodyGlowOpacity = 0.1;
		bodySize = 108;
		bodyColor = '#a5b4fc';
		bodyType = 'moon';
		bodyOpacity = 0.1;
		starsOpacity = 0.3;
		beachFilter = 'brightness(0.35) contrast(1.1) hue-rotate(30deg) saturate(0.5)';
		skyTint = '#0f0b2a';
		skyTintOpacity = 0.85;
	} else if (hour >= 5 && hour < 7) {
		skyGlow = 'radial-gradient(ellipse, rgba(253, 230, 138, 0.5) 0%, rgba(252, 165, 165, 0.15) 40%, rgba(252, 165, 165, 0.03) 70%, transparent 90%)';
		skyGlowSize = 900;
		skyGlowOpacity = 0.8;
		bodyGlow = 'radial-gradient(circle, rgba(253, 230, 138, 0.4) 0%, rgba(253, 230, 138, 0.15) 35%, rgba(253, 230, 138, 0.04) 65%, transparent 100%)';
		bodyGlowOpacity = 0.7;
		bodySize = 150;
		bodyColor = '#fde68a';
		bodyType = 'sun';
		bodyFilter = 'hue-rotate(-25deg) saturate(1.6)';
		bodyOpacity = 0.15;
		starsOpacity = 0;
		beachFilter = 'brightness(0.7) contrast(1.0) hue-rotate(15deg) saturate(0.8)';
		skyTint = '#4338ca';
		skyTintOpacity = 0.3;
	} else if (hour >= 7 && hour < 11) {
		skyGlow = 'radial-gradient(ellipse, rgba(253, 224, 71, 0.3) 0%, rgba(186, 230, 253, 0.08) 50%, rgba(186, 230, 253, 0.02) 70%, transparent 90%)';
		skyGlowSize = 1000;
		skyGlowOpacity = 0.6;
		bodyGlow = 'radial-gradient(circle, rgba(253, 224, 71, 0.3) 0%, rgba(253, 224, 71, 0.1) 35%, rgba(253, 224, 71, 0.02) 65%, transparent 100%)';
		bodyGlowOpacity = 0.55;
		bodySize = 168;
		bodyColor = '#fcd34d';
		bodyType = 'sun';
		bodyOpacity = 0.7;
		starsOpacity = 0;
		beachFilter = 'brightness(1) saturate(1)';
		skyTint = '#3b82f6';
		skyTintOpacity = 0;
	} else if (hour >= 11 && hour < 14) {
		skyGlow = 'radial-gradient(ellipse, rgba(254, 249, 195, 0.3) 0%, rgba(186, 230, 253, 0.06) 50%, rgba(186, 230, 253, 0.01) 70%, transparent 90%)';
		skyGlowSize = 1200;
		skyGlowOpacity = 0.5;
		bodyGlow = 'radial-gradient(circle, rgba(250, 204, 21, 0.25) 0%, rgba(250, 204, 21, 0.08) 35%, rgba(250, 204, 21, 0.02) 65%, transparent 100%)';
		bodyGlowOpacity = 0.45;
		bodySize = 180;
		bodyColor = '#facc15';
		bodyType = 'sun';
		bodyOpacity = 0.85;
		starsOpacity = 0;
		beachFilter = 'brightness(1) saturate(1)';
		skyTint = '#3b82f6';
		skyTintOpacity = 0;
	} else if (hour >= 14 && hour < 17) {
		skyGlow = 'radial-gradient(ellipse, rgba(253, 186, 116, 0.3) 0%, rgba(253, 230, 138, 0.08) 45%, rgba(253, 230, 138, 0.02) 70%, transparent 90%)';
		skyGlowSize = 1000;
		skyGlowOpacity = 0.6;
		bodyGlow = 'radial-gradient(circle, rgba(253, 186, 116, 0.3) 0%, rgba(253, 186, 116, 0.1) 35%, rgba(253, 186, 116, 0.02) 65%, transparent 100%)';
		bodyGlowOpacity = 0.6;
		bodySize = 162;
		bodyColor = '#fdba74';
		bodyType = 'sun';
		bodyOpacity = 0.7;
		starsOpacity = 0;
		beachFilter = 'brightness(0.95) contrast(1.0) hue-rotate(5deg) saturate(0.95)';
		skyTint = '#3b82f6';
		skyTintOpacity = 0;
	} else if (hour >= 17 && hour < 18) {
		skyGlow = 'radial-gradient(ellipse, rgba(251, 146, 60, 0.4) 0%, rgba(253, 164, 175, 0.12) 40%, rgba(253, 164, 175, 0.03) 70%, transparent 90%)';
		skyGlowSize = 900;
		skyGlowOpacity = 0.75;
		bodyGlow = 'radial-gradient(circle, rgba(253, 164, 175, 0.35) 0%, rgba(253, 164, 175, 0.12) 35%, rgba(253, 164, 175, 0.03) 65%, transparent 100%)';
		bodyGlowOpacity = 0.6;
		bodySize = 144;
		bodyColor = '#fb923c';
		bodyType = 'sun';
		bodyFilter = 'hue-rotate(-35deg) saturate(1.8)';
		bodyOpacity = 0.55;
		starsOpacity = 0.15;
		beachFilter = 'brightness(0.75) contrast(1.05) hue-rotate(15deg) saturate(0.85)';
		skyTint = '#1e1b4b';
		skyTintOpacity = 0.35;
	} else if (hour >= 18 && hour < 19) {
		skyGlow = 'radial-gradient(ellipse, rgba(251, 146, 60, 0.15) 0%, transparent 55%)';
		skyGlowSize = 800;
		skyGlowOpacity = 0.3;
		bodyGlow = 'radial-gradient(circle, rgba(253, 164, 175, 0.1) 0%, transparent 60%)';
		bodyGlowOpacity = 0.1;
		bodySize = 136;
		bodyColor = '#fb923c';
		bodyType = 'sun';
		bodyFilter = 'hue-rotate(-35deg) saturate(1.8)';
		bodyOpacity = 0.1;
		starsOpacity = 0.3;
		beachFilter = 'brightness(0.55) contrast(1.1) hue-rotate(25deg) saturate(0.7)';
		skyTint = '#0f0b2a';
		skyTintOpacity = 0.6;
	} else if (hour >= 19 && hour < 20) {
		skyGlow = 'radial-gradient(ellipse, rgba(199, 210, 254, 0.1) 0%, transparent 55%)';
		skyGlowSize = 700;
		skyGlowOpacity = 0.3;
		bodyGlow = 'radial-gradient(circle, rgba(199, 210, 254, 0.1) 0%, transparent 60%)';
		bodyGlowOpacity = 0.15;
		bodySize = 120;
		bodyColor = '#c7d2fe';
		bodyType = 'moon';
		bodyOpacity = 0.15;
		starsOpacity = 0.4;
		beachFilter = 'brightness(0.4) contrast(1.15) hue-rotate(30deg) saturate(0.6)';
		skyTint = '#0f0b2a';
		skyTintOpacity = 0.75;
	} else if (hour >= 20 && hour < 21) {
		skyGlow = 'radial-gradient(ellipse, rgba(199, 210, 254, 0.2), transparent 60%)';
		skyGlowSize = 700;
		skyGlowOpacity = 0.4;
		bodyGlow = 'radial-gradient(circle, rgba(199, 210, 254, 0.15) 0%, rgba(199, 210, 254, 0.05) 40%, transparent 80%)';
		bodyGlowOpacity = 0.35;
		bodySize = 120;
		bodyColor = '#c7d2fe';
		bodyType = 'moon';
		bodyOpacity = 0.5;
		starsOpacity = 0.6;
		beachFilter = 'brightness(0.3) contrast(1.2) hue-rotate(30deg) saturate(0.5)';
		skyTint = '#0f0b2a';
		skyTintOpacity = 0.85;
	} else {
		skyGlow = 'radial-gradient(ellipse, rgba(165, 180, 252, 0.15), transparent 55%)';
		skyGlowSize = 600;
		skyGlowOpacity = 0.3;
		bodyGlow = 'radial-gradient(circle 200px, rgba(199, 210, 254, 0.18), transparent)';
		bodyGlowOpacity = 0.25;
		bodySize = 108;
		bodyColor = '#a5b4fc';
		bodyType = 'moon';
		bodyOpacity = 0.65;
		starsOpacity = 0.7;
		beachFilter = 'brightness(0.25) contrast(1.2) hue-rotate(30deg) saturate(0.45)';
		skyTint = '#0f0b2a';
		skyTintOpacity = 0.9;
	}

	const altitude = (y - yPeak) / (yBase - yPeak);
	bodySize = bodySize * (0.5 + 0.5 * altitude);

	const nightProgress = isDay ? 0 : progress;
	const starsDriftX = nightProgress * 12 - 6;
	const starsDriftY = -(nightProgress * 6 - 3);

	return {
		skyGlow,
		skyGlowSize,
		skyGlowOpacity,
		bodyGlow,
		bodyGlowOpacity,
		x,
		y,
		bodySize,
		bodyColor,
		bodyType,
		bodyFilter,
		bodyOpacity,
		starsOpacity,
		starsDriftX,
		starsDriftY,
		beachFilter,
		skyTint,
		skyTintOpacity,
		isDay,
	};
};

export const DynamicBackground: FC = () => {
	const isAdminRoute = useIsAdminRoute();
	const [scene, setScene] = useState(() => getSceneState(new Date().getHours()));
	const [ready, setReady] = useState(false);

	useEffect(() => {
		requestAnimationFrame(() => setReady(true));

		const updateTime = () => setScene(getSceneState(new Date().getHours()));

		const timeInterval = window.setInterval(updateTime, 60_000);

		return () => {
			clearInterval(timeInterval);
		};
	}, []);

	if (isAdminRoute) return null;

	return (
		<>
			<style>
				{`
					html.summer-theme,
					html.summer-theme body {
						background: transparent !important;
					}

					.summer-theme [data-testid="recognition-content-wrapper"],
					.summer-theme [data-testid="homepage-content-wrapper"],
					.summer-theme [data-testid="right-content-wrapper"],
					.summer-theme [data-testid="left-sidebar-wrapper"],
					.summer-theme [data-testid="right-content-centerer"],
					.summer-theme [data-testid="main-feed-column"],
					.summer-theme [data-component="recap-page-layout"],
					.summer-theme #nav-wrapper,
					.summer-theme #root > div {
						background: transparent !important;
						background-color: transparent !important;
					}

					@keyframes summer-star-twinkle {
						0%, 100% { opacity: 0.3; transform: scale(1); }
						50% { opacity: 1; transform: scale(1.3); }
					}
				`}
			</style>

			<div
				style={{
					position: 'fixed',
					top: 0,
					left: 0,
					width: '100vw',
					height: '100vh',
					zIndex: -1,
					pointerEvents: 'none',
					opacity: ready ? 1 : 0,
					transition: 'opacity 0.3s ease',
					overflow: 'hidden',
				}}
			>
				<div
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						height: '100%',
						background: 'linear-gradient(180deg, #0274E8 0%, #3EB2F8 100%)',
						transition: 'background 2s ease, opacity 2s ease',
					}}
				/>

				<div
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						height: '100%',
						backgroundColor: scene.skyTint,
						opacity: scene.skyTintOpacity,
						transition: 'opacity 2s ease, background-color 2s ease',
					}}
				/>

				<div
					style={{
						position: 'absolute',
						left: `${scene.x}%`,
						top: `${scene.y}%`,
						width: `${scene.skyGlowSize}px`,
						height: `${scene.skyGlowSize}px`,
						transform: 'translate(-50%, -50%)',
						borderRadius: '50%',
						background: scene.skyGlow,
						opacity: scene.skyGlowOpacity,
						transition: 'left 2s ease, top 2s ease, width 2s ease, height 2s ease, opacity 2s ease, background 2s ease',
					}}
				/>

				<div
					style={{
						position: 'absolute',
						left: `${scene.x}%`,
						top: `${scene.y}%`,
						width: '400px',
						height: '400px',
						transform: 'translate(-50%, -50%)',
						borderRadius: '50%',
						background: scene.bodyGlow,
						opacity: scene.bodyGlowOpacity,
						transition: 'left 2s ease, top 2s ease, opacity 2s ease, background 2s ease',
					}}
				/>

				<div
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						height: '100%',
						opacity: scene.starsOpacity,
						transform: `translate(${scene.starsDriftX}%, ${scene.starsDriftY}%)`,
						transition: 'opacity 3s ease, transform 2s ease',
					}}
				>
					{STARS.map((star, i) => (
						<div
							key={i}
							style={{
								position: 'absolute',
								left: `${star.x}%`,
								top: `${star.y}%`,
								width: `${star.size}px`,
								height: `${star.size}px`,
								borderRadius: '50%',
								backgroundColor: '#e0e7ff',
								boxShadow: `0 0 ${star.size + 2}px rgba(224, 231, 255, 0.6)`,
								animation: `summer-star-twinkle ${2.5 + star.delay}s ease-in-out ${star.delay}s infinite`,
							}}
						/>
					))}
				</div>

				<div
					style={{
						position: 'absolute',
						left: `${scene.x}%`,
						top: `${scene.y}%`,
						transform: 'translate(-50%, -50%)',
						opacity: scene.bodyOpacity,
						transition: 'left 2s ease, top 2s ease, opacity 2s ease, filter 2s ease',
						filter: `drop-shadow(0 0 20px ${scene.bodyColor})`,
					}}
				>
					{scene.bodyType === 'sun' ? <SunSvg size={scene.bodySize} filter={scene.bodyFilter} /> : <MoonSvg size={scene.bodySize} />}
				</div>

				<img
					src={BeachBg}
					alt=""
					style={{
						position: 'absolute',
						bottom: 0,
						left: 0,
						width: '100%',
						filter: scene.beachFilter,
						transition: 'filter 2s ease',
						color: 'transparent',
						pointerEvents: 'none',
					}}
				/>

				<img
					src={GreenTube}
					alt=""
					style={{
						position: 'absolute',
						top: '76.41%',
						left: '4.56%',
						width: '6.06vw',
						pointerEvents: 'none',
					}}
				/>

				<img
					src={Flamingo}
					alt=""
					style={{
						position: 'absolute',
						top: '72%',
						left: '10%',
						width: '9.5625vw',
						pointerEvents: 'none',
					}}
				/>

				<img
					src={PurpleTube}
					alt=""
					style={{
						position: 'absolute',
						top: '71.51%',
						left: '21.09%',
						width: '3.26vw',
						pointerEvents: 'none',
					}}
				/>

				<img
					src={PinkTube}
					alt=""
					style={{
						position: 'absolute',
						top: '74.30%',
						left: '24.34%',
						width: '5.15vw',
						pointerEvents: 'none',
					}}
				/>

				<img
					src={RedTube}
					alt=""
					style={{
						position: 'absolute',
						top: '81.01%',
						left: '26.91%',
						width: '9.46vw',
						pointerEvents: 'none',
					}}
				/>

				<img
					src={YellowFloat}
					alt=""
					style={{
						position: 'absolute',
						top: '76.22%',
						left: '56.03%',
						width: '13.65vw',
						pointerEvents: 'none',
					}}
				/>

				<img
					src={OrangeTube}
					alt=""
					style={{
						position: 'absolute',
						top: '71.57%',
						left: '72.03%',
						width: '3.49vw',
						pointerEvents: 'none',
					}}
				/>

				<img
					src={MagentaTube}
					alt=""
					style={{
						position: 'absolute',
						top: '78.88%',
						left: '70.94%',
						width: '8.09vw',
						pointerEvents: 'none',
					}}
				/>

				<img
					src={Unicorn}
					alt=""
					style={{
						position: 'absolute',
						top: '70.06%',
						left: '75.31%',
						width: '11.80vw',
						pointerEvents: 'none',
					}}
				/>

				<img
					src={GreenTube}
					alt=""
					style={{
						position: 'absolute',
						top: '72.75%',
						left: '82.15%',
						width: '4.50vw',
						pointerEvents: 'none',
					}}
				/>

				<img
					src={BlueTube}
					alt=""
					style={{
						position: 'absolute',
						top: '80%',
						left: '86%',
						width: '8.46875vw',
						pointerEvents: 'none',
					}}
				/>

				<img
					src={Pineapple}
					alt=""
					style={{
						position: 'absolute',
						top: '70.53%',
						left: '91.76%',
						width: '7.15vw',
						pointerEvents: 'none',
					}}
				/>
			</div>
		</>
	);
};
